import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children, initialCurrency = 'Toman', getBasis, updateBasisCurrency }) {
    const [currency, setCurrency] = useState(initialCurrency || 'Toman');
    const [loading, setLoading] = useState(false);

    /**
     * Sync currency from backend
     */
    const syncCurrencyFromBackend = useCallback(async () => {
        if (!getBasis) return;

        try {
            setLoading(true);

            const basis = await getBasis();

            if (basis?.currency) {
                setCurrency(basis.currency);
            }
        } catch (error) {
            console.error('Failed to sync currency from backend:', error);
        } finally {
            setLoading(false);
        }
    }, [getBasis]);

    /**
     * Change currency locally + update backend
     */
    const changeCurrency = async (nextCurrency) => {
        if (!nextCurrency || nextCurrency === currency) return;

        const previousCurrency = currency;

        try {
            setCurrency(nextCurrency);

            if (updateBasisCurrency) {
                await updateBasisCurrency(nextCurrency);
            }
        } catch (error) {
            console.error('Failed to update currency:', error);

            // rollback if backend update fails
            setCurrency(previousCurrency);
        }
    };

    /**
     * Sync when user focuses tab/window again
     * Useful for multi-tab / multi-device changes
     */
    useEffect(() => {
        const handleFocus = () => {
            syncCurrencyFromBackend();
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [syncCurrencyFromBackend]);

    const value = {
        currency,
        setCurrency: changeCurrency,
        loading,
        syncCurrencyFromBackend
    };

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    const context = useContext(CurrencyContext);

    if (!context) {
        throw new Error('useCurrency must be used inside CurrencyProvider');
    }

    return context;
}
